import type {
    PageStackBlockValueBlockMap,
    PageStackBlockValueItem,
    PageStackBlockValueRecordMap,
    PageStackBlockValueStackMap
} from '@lighthouse/core'

type BlockValueContext = {
    stackId: string
    blockId: string
    recordId?: string
    columnId?: string
}

type BlockValueBlockContext = Omit<BlockValueContext, 'stackId'>

type BlockValueBlockOnlyContext = {
    stackId: string
    blockId: string
}

const isBlockValueItem = (value: unknown): value is PageStackBlockValueItem => {
    return !!value && typeof value === 'object' && 'blockType' in (value as PageStackBlockValueItem)
}

const ensureRecordMap = (value: PageStackBlockValueItem | PageStackBlockValueRecordMap | undefined): PageStackBlockValueRecordMap => {
    if (!value || isBlockValueItem(value)) {
        return {}
    }
    return value
}

const ensureBlockMap = (stackMap: PageStackBlockValueStackMap, stackId: string): PageStackBlockValueBlockMap => {
    if (!stackMap[stackId]) {
        stackMap[stackId] = {}
    }
    return stackMap[stackId]
}

export const getBlockValueByContext = (
    stackMap: PageStackBlockValueStackMap | undefined,
    { stackId, blockId, recordId, columnId }: BlockValueContext
): PageStackBlockValueItem | undefined => {
    if (!stackMap) {
        return
    }
    const blockLayer = stackMap[stackId]?.[blockId]
    if (!blockLayer) {
        return
    }
    if (!recordId) {
        return isBlockValueItem(blockLayer) ? blockLayer : undefined
    }
    if (isBlockValueItem(blockLayer)) {
        return undefined
    }
    const recordLayer = blockLayer[recordId]
    if (!recordLayer) {
        return
    }
    if (!columnId) {
        return isBlockValueItem(recordLayer) ? recordLayer : undefined
    }
    if (isBlockValueItem(recordLayer)) {
        return undefined
    }
    return recordLayer[columnId]
}

export const getBlockValueFromBlockMap = (
    blockMap: PageStackBlockValueBlockMap | undefined,
    { blockId, recordId, columnId }: BlockValueBlockContext
): PageStackBlockValueItem | undefined => {
    if (!blockMap) {
        return
    }
    const blockLayer = blockMap[blockId]
    if (!blockLayer) {
        return
    }
    if (!recordId) {
        return isBlockValueItem(blockLayer) ? blockLayer : undefined
    }
    if (isBlockValueItem(blockLayer)) {
        return undefined
    }
    const recordLayer = blockLayer[recordId]
    if (!recordLayer) {
        return
    }
    if (!columnId) {
        return isBlockValueItem(recordLayer) ? recordLayer : undefined
    }
    if (isBlockValueItem(recordLayer)) {
        return undefined
    }
    return recordLayer[columnId]
}

export const setBlockValueAtLeaf = (stackMap: PageStackBlockValueStackMap, context: BlockValueContext, value: PageStackBlockValueItem) => {
    const { stackId, blockId, recordId, columnId } = context
    const blockLayer = ensureBlockMap(stackMap, stackId)

    if (!recordId) {
        blockLayer[blockId] = value
        return
    }

    const recordMap = ensureRecordMap(blockLayer[blockId])
    blockLayer[blockId] = recordMap

    if (!columnId) {
        recordMap[recordId] = value
        return
    }

    const existingRecord = recordMap[recordId]
    const columnMap = !existingRecord || isBlockValueItem(existingRecord) ? {} : existingRecord
    columnMap[columnId] = value
    recordMap[recordId] = columnMap
}

export const deleteBlockValueAtLeaf = (
    stackMap: PageStackBlockValueStackMap,
    { stackId, blockId, recordId, columnId }: BlockValueContext
) => {
    const blockLayer = stackMap[stackId]
    if (!blockLayer) {
        return
    }
    const blockValue = blockLayer[blockId]
    if (!blockValue) {
        return
    }

    if (!recordId) {
        delete blockLayer[blockId]
    } else if (isBlockValueItem(blockValue)) {
        return
    } else if (!columnId) {
        delete blockValue[recordId]
    } else {
        const recordLayer = blockValue[recordId]
        if (!recordLayer || isBlockValueItem(recordLayer)) {
            return
        }
        delete recordLayer[columnId]
        if (Object.keys(recordLayer).length === 0) {
            delete blockValue[recordId]
        }
    }

    if (Object.keys(blockLayer).length === 0) {
        delete stackMap[stackId]
    }
}

export const clearBlockValueByBlock = (stackMap: PageStackBlockValueStackMap, { stackId, blockId }: BlockValueBlockOnlyContext) => {
    const blockLayer = stackMap[stackId]
    if (!blockLayer) {
        return
    }
    if (blockLayer[blockId]) {
        delete blockLayer[blockId]
    }
    if (Object.keys(blockLayer).length === 0) {
        delete stackMap[stackId]
    }
}
